/**
 * PinGate - locks the client app behind the 4-digit PIN on every fresh launch.
 *
 * Trigger conditions:
 *   - User is authenticated (token present)
 *   - User has a PIN configured (cached locally, refreshed from server on first
 *     successful unlock)
 *   - Current session is NOT yet marked unlocked (sessionStorage flag - cleared
 *     when the PWA is fully closed)
 *
 * Behaviour:
 *   - Renders a numpad PIN entry over the children. Submit calls loginWithPin,
 *     which (a) validates the PIN server-side, (b) refreshes the JWT, and
 *     (c) marks the session unlocked.
 *   - "Use SMS code instead" → logs out (keeps remembered phone) and routes to
 *     the regular login screen so the user can re-auth via SMS.
 */

import { useEffect, useRef, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, Delete } from 'lucide-react';
import {
  getHasPinCached,
  isPinUnlocked,
  loginWithPin,
  getRememberedPhone,
  getStoredClient,
  logout,
  fetchSettings,
  setHasPinCached,
} from '@/services/api/client-auth';
import { useV4 } from './i18n';

export default function PinGate({ children }: { children: ReactNode }) {
  const { accent } = useV4();
  const navigate = useNavigate();

  // First-render decision needs to happen synchronously so the children never
  // briefly flash before the gate appears.
  const [locked, setLocked] = useState(() => getHasPinCached() && !isPinUnlocked());
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cross-check with server - covers the case where the user set up a PIN on
  // another device since the cache was last refreshed.
  useEffect(() => {
    let cancelled = false;
    if (locked) return; // already locked, no need to ask the server
    fetchSettings().then(s => {
      if (cancelled) return;
      setHasPinCached(s.hasPin);
      if (s.hasPin && !isPinUnlocked()) setLocked(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [locked]);

  useEffect(() => {
    if (locked) inputRef.current?.focus();
  }, [locked]);

  const submit = async (value: string) => {
    if (!/^\d{4}$/.test(value) || busy) return;
    const phone = getRememberedPhone();
    if (!phone) {
      // No phone to verify against - punt to SMS flow.
      logout();
      navigate('/client/login', { replace: true });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await loginWithPin(phone, value);
      setLocked(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Incorrect PIN');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const onTap = (d: string) => {
    if (busy) return;
    setError(null);
    setPin(p => {
      const next = (p + d).slice(0, 4);
      if (next.length === 4) submit(next);
      return next;
    });
  };

  const onBack = () => {
    setError(null);
    setPin(p => p.slice(0, -1));
  };

  const onUseSms = () => {
    logout({ keepRememberedPhone: true });
    navigate('/client/login', { replace: true });
  };

  if (!locked) return <>{children}</>;

  const client = getStoredClient();
  const greeting = client?.displayName ? `Welcome back, ${client.displayName.split(' ')[0]}` : 'Welcome back';

  return (
    <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-background px-6 py-10">
      <div className="flex flex-col items-center text-center max-w-sm w-full">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm"
          style={{ background: accent.tint, color: accent.text }}
        >
          <KeyRound className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-lg font-semibold text-foreground">{greeting}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Enter your 4-digit PIN to continue</p>

        {/* PIN dots */}
        <div className="mt-7 flex items-center gap-3">
          {[0, 1, 2, 3].map(i => {
            const filled = pin.length > i;
            return (
              <span
                key={i}
                className="h-3.5 w-3.5 rounded-full border-2 transition-all"
                style={{
                  borderColor: filled ? accent.primary : 'var(--border)',
                  background: filled ? accent.primary : 'transparent',
                }}
              />
            );
          })}
        </div>

        {/* Hidden input so keyboards work on desktop / iPad with attached keyboard */}
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          pattern="\d*"
          maxLength={4}
          value={pin}
          onChange={e => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
            setPin(v);
            setError(null);
            if (v.length === 4) submit(v);
          }}
          className="absolute h-0 w-0 opacity-0"
          aria-label="PIN"
          autoFocus
        />

        {error && <p className="mt-4 text-xs font-medium text-rose-600">{error}</p>}
        {busy && <Loader2 className="mt-4 h-4 w-4 animate-spin text-muted-foreground" />}

        {/* Numpad - primary input on mobile */}
        <div className="mt-7 grid grid-cols-3 gap-3 w-full max-w-[260px]">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
            <button
              key={d}
              type="button"
              onClick={() => onTap(d)}
              disabled={busy}
              className="h-14 rounded-2xl border border-border bg-card text-2xl font-semibold text-foreground active:scale-95 transition-transform disabled:opacity-50"
            >
              {d}
            </button>
          ))}
          <div />
          <button
            type="button"
            onClick={() => onTap('0')}
            disabled={busy}
            className="h-14 rounded-2xl border border-border bg-card text-2xl font-semibold text-foreground active:scale-95 transition-transform disabled:opacity-50"
          >
            0
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={busy || pin.length === 0}
            className="h-14 rounded-2xl border border-border bg-card text-muted-foreground hover:text-foreground active:scale-95 transition-transform disabled:opacity-30 flex items-center justify-center"
            aria-label="Backspace"
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>

        <button
          type="button"
          onClick={onUseSms}
          disabled={busy}
          className="mt-8 text-xs font-medium text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
        >
          Use SMS code instead
        </button>
      </div>
    </div>
  );
}
